/**
 * The six body regions a posture report is organised by, and which measurement
 * belongs in which.
 *
 * Port of KinetiQE/src/lib/pose/postureRegions.ts — the kiosk prints the same
 * grouping and writes its AI prompt from it, and this copy lays out the web
 * report from the readings the kiosk already graded. It must not disagree:
 * KinetiQE/src/lib/pose/webReportParity.test.ts loads this file and fails if the
 * regions or the grouping ever diverge.
 *
 * Every item a region lists is shown, measured or not. A reading that could not
 * be taken prints "Not measured" — never a blank that reads as "fine". Some
 * items are listed only to say so permanently: a pose model has no ASIS/PSIS or
 * spinal landmarks, so anterior/posterior pelvic tilt and thoracic/lumbar
 * curvature are not measurements this system makes.
 */

export const POSTURE_REGIONS = [
  {
    id: "headNeck", title: "Head & neck",
    items: [
      { label: "Forward head (ear ahead of shoulder)", names: ["Forward head"] },
      { label: "Neck angle — craniovertebral, ear to C7", names: ["Craniovertebral angle"] },
      { label: "Cervical flexion / extension", names: ["Cervical flexion/extension"] },
      { label: "Head tilt", names: ["Head tilt"] },
      { label: "Head centre deviation", names: ["Head center deviation"] },
    ],
  },
  {
    id: "shoulders", title: "Shoulders",
    items: [
      { label: "Shoulder height and slope", names: ["Shoulder tilt"], offsets: ["Shoulder level difference"] },
      { label: "Forward / rounded shoulder", names: ["Forward shoulder"] },
    ],
  },
  {
    id: "spineTrunk", title: "Spine & trunk",
    items: [
      { label: "Trunk lateral lean and spinal asymmetry", names: ["Spine deviation"] },
      { label: "Trunk lean, forward / backward", names: ["Trunk lean"] },
      { label: "Body midline", names: ["Body midline deviation"] },
      { label: "Shoulder-to-pelvis relationship", names: ["Shoulder-to-pelvis relationship"] },
      {
        label: "Thoracic posture (rounding)", names: [],
        unmeasurable: "Spinal curvature cannot be read from joint landmarks. It is not measured.",
      },
      {
        label: "Lumbar posture (curvature)", names: [],
        unmeasurable: "Spinal curvature cannot be read from joint landmarks. It is not measured.",
      },
    ],
  },
  {
    id: "pelvisHips", title: "Pelvis & hips",
    items: [
      { label: "Pelvic obliquity and hip height", names: ["Pelvic obliquity"], offsets: ["Hip level difference"] },
      { label: "Pelvic shift", names: ["Pelvic shift"] },
      { label: "Hip position against the plumb line", names: ["Hip position"] },
      { label: "Pelvic-to-trunk relationship", names: ["Pelvic-to-trunk relationship"] },
      {
        label: "Anterior / posterior pelvic tilt", names: [],
        unmeasurable: "Pelvic tilt needs the ASIS and PSIS landmarks, which the pose model does not provide. It is not measured.",
      },
    ],
  },
  {
    id: "kneeLowerLimb", title: "Knee & lower limb",
    items: [
      {
        label: "Hip–knee–ankle alignment — valgus / varus",
        names: ["Left knee deviation", "Right knee deviation"],
        offsets: ["Knee level difference", "Ankle level difference"],
      },
      { label: "Leg tilt — hip-to-ankle line against vertical", names: ["Left leg tilt", "Right leg tilt", "Leg tilt asymmetry"] },
      { label: "Knee flexion / hyperextension", names: ["Left knee flexion", "Right knee flexion"] },
      { label: "Foot progression angle", names: ["Left foot progression", "Right foot progression"] },
      { label: "Heel symmetry", names: ["Heel symmetry"] },
    ],
  },
  {
    id: "arms", title: "Arms",
    items: [
      { label: "Carrying angle (elbow)", names: ["Left elbow angle", "Right elbow angle"], offsets: ["Elbow level difference"] },
      { label: "Arm symmetry", names: ["Arm symmetry"] },
      { label: "Wrist position", names: [], offsets: ["Wrist level difference"] },
    ],
  },
];

export const REGION_IDS = POSTURE_REGIONS.map(r => r.id);

/** The kinetic chain the report closes on, in the order the body carries load. */
export const KINETIC_CHAIN = ['Head', 'Spine', 'Pelvis', 'Knee', 'Ankle', 'Foot'];

const RANK = { normal: 0, mild: 1, moderate: 2, severe: 3 };

/**
 * A view whose markers are still placeholders carries no measurement, and its
 * angles must not enter a region as if it did.
 */
function usable(view) {
  return !(view.landmarksSeeded && !view.landmarksEdited);
}

function severityOf(angle) {
  return angle.severity ?? (angle.normal === false ? 'mild' : 'normal');
}

/** Every region with its rows filled from the views' readings. */
export function regionsFor(views) {
  const list = (views ?? []).filter(usable);
  return POSTURE_REGIONS.map(def => {
    let measured = 0;
    let flagged = 0;
    let worst = null;

    const rows = def.items.map(item => {
      const readings = [];
      const offsets = [];
      for (const view of list) {
        const label = view.label ?? view.view ?? '';
        for (const angle of view.angles ?? []) {
          if (angle?.name && item.names.includes(angle.name) && typeof angle.degrees === 'number') {
            readings.push({ view: label, angle });
          }
        }
        for (const offset of view.offsets ?? []) {
          if (offset?.name && item.offsets?.includes(offset.name) && typeof offset.cm === 'number') {
            offsets.push({ view: label, offset });
          }
        }
      }
      for (const { angle } of readings) {
        measured++;
        const sev = severityOf(angle);
        if (sev !== 'normal') flagged++;
        if (worst === null || (RANK[sev] ?? 0) > (RANK[worst] ?? 0)) worst = sev;
      }
      return {
        label: item.label,
        readings,
        offsets,
        measured: readings.length > 0 || offsets.length > 0,
        ...(item.unmeasurable ? { unmeasurable: item.unmeasurable } : {}),
      };
    });

    return { id: def.id, title: def.title, rows, measured, flagged, worst };
  });
}

/** True when at least one region has a measured reading. */
export function hasRegionReadings(regions) {
  return regions.some(r => r.measured > 0);
}

/** "Left 12.4° · Right 3.1°" — a row's readings on one line. */
export function formatReadings(row) {
  return row.readings.map(({ angle }) => {
    const side = /^(Left|Right)\b/.exec(angle.name ?? '')?.[1];
    const value = `${angle.degrees.toFixed(1)}°`;
    return side ? `${side} ${value}` : value;
  }).join(' · ');
}

/**
 * The reference for a row, as the tables print it: "0° (normal up to 8°)",
 * or the range when 0° is not normal. Worded as lib/report/reference.js does;
 * written out here because this file is kept free of imports (the kiosk's
 * parity test loads it on its own).
 */
export function formatReference(row) {
  const r = row.readings.find(x => x.angle.normalRange)?.angle.normalRange;
  if (!r) return '—';
  const deg = v => `${String(v).replace('-', '−')}°`;
  if (r[0] <= 0 && r[1] >= 0) return r[0] === 0 ? `0° (normal up to ${deg(r[1])})` : `0° (normal ${deg(r[0])} to ${deg(r[1])})`;
  return r[0] < 0 || r[1] < 0 ? `${deg(r[0])} to ${deg(r[1])}` : `${r[0]}–${r[1]}°`;
}

/** The worst severity among a row's readings, or null when it has none. */
export function rowSeverity(row) {
  let worst = null;
  for (const { angle } of row.readings) {
    const sev = severityOf(angle);
    if (worst === null || (RANK[sev] ?? 0) > (RANK[worst] ?? 0)) worst = sev;
  }
  return worst;
}

/** The directions stated on a row's out-of-range readings, de-duplicated. */
export function rowDirections(row) {
  const seen = new Set();
  for (const { angle } of row.readings) {
    if (angle.direction && !seen.has(angle.direction)) seen.add(angle.direction);
  }
  return Array.from(seen);
}
